import { gql } from '@apollo/client'

export const FRAGMENT_CART_SYNC_NOTIFICATIONS = gql`
  fragment FragmentNotifications on Cart {
    notifications {
      ... on CartLineInvalidQuantity {
        code
        cartLine {
          id
					sku
					variant {
						id
						minSaleUnits
						name
						stock
					}
					product {
						id
						name
					}
        }
        oldQuantity
        newQuantity
        deleted
      }
      ... on CartLineProductNotAvailable {
        code
        cartLine {
          id
					sku
					variant {
						id
						name
					}
					product {
						id
						name
					}
        }
      }
      ... on CartLineVariantNotAvailable {
        code
        cartLine {
          id
					sku
					variant {
						id
						name
					}
					product {
						id
						name
					}
        }
      }
    }
  }
`

export const FRAGMENT_PRODUCT_REQUIRED = gql`
  fragment FragmentProductRequired on Product {
    bigcommerceId
    brand {
      id
      name
      slug
    }
    ean
    gtin
    inventoryTracking
    name
    productType
    slug
    sku
  }
`

export const FRAGMENT_PRICE = gql`
  fragment FragmentPrice on Variant {
    price {
      discount
      salePrice: discountedPrice
      retailPrice: fullPrice
    }
  }
`

export const FRAGMENT_VARIANT_REQUIRED = gql`
  fragment FragmentVariantRequired on Variant {
    id
    bigcommerceId
    ean
    gtin
    deliveryDate
    deliveryType
    onDemandDay
    media {
      mediaType
      medium(variant: "pdp")
    }
    minSaleUnits
    qtyPerBox
    name
    ...FragmentPrice
    sku
    stock
    stockWarning
  }
  ${FRAGMENT_PRICE}
`

const FRAGMENT_SERVICES = gql`
  fragment FragmentServices on CartLine {
    services {
      cartLineType
      extendedSalePrice
      id
      listPrice
      quantity
      product {
        ...FragmentProductRequired
        labels {
          name
        }
      }
      variant {
        ...FragmentVariantRequired
      }
    }
  }
  ${FRAGMENT_PRODUCT_REQUIRED}
  ${FRAGMENT_VARIANT_REQUIRED}
`

export const FRAGMENT_CART_LINES = gql`
  fragment FragmentCartLines on Cart {
    cartLines {
      id
      quantity
      cartLineType
      listPrice
      extendedSalePrice
      product {
        ...FragmentProductRequired
        labels {
          name
        }
      }
      variant {
        ...FragmentVariantRequired
      }
      ...FragmentServices
    }
  }
  ${FRAGMENT_PRODUCT_REQUIRED}
  ${FRAGMENT_VARIANT_REQUIRED}
  ${FRAGMENT_SERVICES}
`

export const FRAGMENT_CART = gql`
  fragment FragmentCart on Cart {
    id
    bigcommerceId
    customer {
      bigcommerceId
    }
    promotions {
      id
      name
      rules {
        ruleableType
        ruleable
      }
    }
    promotionsDiscountAmount
    discountAmount
    subtotalExTax
    totalExTax
    multipleDeliveryDates
    deliveryDate
    deliveryDateOptions
    deliveryType
    deliverySchedules {
      name
      agency
      minimumPurchase
      minimumPurchaseFrozen
      agencyText
    }
    ...FragmentCartLines
    ...FragmentNotifications
  }
  ${FRAGMENT_CART_SYNC_NOTIFICATIONS}
  ${FRAGMENT_CART_LINES}
`

export const FRAGMENT_BOUGHT_TOGETHER_VARIANTS = gql`
  fragment FragmentboughtTogether on Cart {
    boughtTogether {
      ...FragmentVariantRequired
      product {
        ...FragmentProductRequired
        description
        ingredients
        seo
        labels {
          name
        }
      }
    }
  }
  ${FRAGMENT_VARIANT_REQUIRED}
  ${FRAGMENT_PRODUCT_REQUIRED}
`

export const FRAGMENT_DIGITAL_PRODUCT_REQUIRED = gql`
  fragment FragmentDigitalProduct on DigitalProduct {
    name
    price
    sku
  }
`

export const FRAGMENT_RELATED_VARIANTS = gql`
  fragment FragmentRelatedVariants on Product {
    related {
      ...FragmentVariantRequired
      product {
        ...FragmentProductRequired
        labels {
          name
        }
        description
        ingredients
        nutrition {
          name
          value
          nutritionType
        }
        infoSpecifications {
          name
          value
        }
      }
    }
  }
  ${FRAGMENT_VARIANT_REQUIRED}
  ${FRAGMENT_PRODUCT_REQUIRED}
`

const FRAGMENT_CART_SYNC_PRODUCT_REQUIRED = gql`
  fragment FragmentCartSyncProductRequired on Product {
    id
    bigcommerceId
    brand {
      name
    }
    ean
    gtin
    name
    productType
    slug
    sku
  }
`

const FRAGMENT_CART_SYNC_VARIANT_REQUIRED = gql`
  fragment FragmentCartSyncVariantRequired on Variant {
    id
    bigcommerceId
    ean
    gtin
    deliveryType
    deliveryDate
    deliveryDays
    onDemandDay
    media {
      mediaType
      medium
    }
    minSaleUnits
    name
    ...FragmentPrice
    sku
    stock
    stockWarning
  }
  ${FRAGMENT_PRICE}
`

const FRAGMENT_CART_SYNC_SERVICES = gql`
  fragment FragmentCartSyncServices on CartLine {
    bigcommerceId
    cartLineType
    extendedListPrice
    extendedOriginalPrice
    extendedSalePrice
    id
    listPrice
    originalPrice
    quantity
    salePrice
    product {
      ...FragmentCartSyncProductRequired
      brand {
        name
        logo
      }
      labels {
        name
      }
    }
    variant {
      ...FragmentCartSyncVariantRequired
    }
  }
  ${FRAGMENT_CART_SYNC_PRODUCT_REQUIRED}
  ${FRAGMENT_CART_SYNC_VARIANT_REQUIRED}
`

export const FRAGMENT_CART_SYNC_CART_LINES = gql`
  fragment FragmentCartSyncCartLines on Cart {
    cartLines {
      id
      bigcommerceId
      quantity
      extendedListPrice
      originalPrice
      extendedOriginalPrice
      extendedSalePrice
      salePrice
      cartLineType
      listPrice
      services {
        ...FragmentCartSyncServices
      }
      product {
        ...FragmentCartSyncProductRequired
        brand {
          name
          logo
        }
        labels {
          name
        }
      }
      variant {
        ...FragmentCartSyncVariantRequired
      }
    }
  }
  ${FRAGMENT_CART_SYNC_PRODUCT_REQUIRED}
  ${FRAGMENT_CART_SYNC_VARIANT_REQUIRED}
  ${FRAGMENT_CART_SYNC_SERVICES}
`

export const FRAGMENT_ADDRESS = gql`
  fragment FragmentAddress on Address {
    id
    bigcommerceId
    addressType
    city
    countryCode
    firstName
    lastName
    postalCode
    region
    street1
    street2
  }
`


export const FRAGMENT_ORDERS = gql`
  fragment FragmentOrders on Customer {
    orders {
      edges {
        cursor
        node {
          bigcommerceId
          dateCreated
          orderStatus
          storeCreditAmount
          totalExTax
          orderLines {
            orderId
            productName
            quantity
            priceExTax
            brandName
            # variantName
            variantSku
            product {
              inventoryTracking
            }
            variant {
              id
              name
              importStatus
              media {
                medium(variant: "grid")
                mediaType
              }
              minSaleUnits
              stock
              stockWarning
            }
          }
        }
      }
    }
  }
`
